/**
 * The editor pane of the template library — `email-templates.html`'s `#tplForm`
 * ported. Remounted (via the parent's `key`) on every selection change rather
 * than synced with an effect: a contentEditable body cannot be a controlled
 * React value without fighting the cursor, so it is read imperatively at save
 * time instead, and a fresh mount per template is the simplest way to get a
 * clean starting `innerHTML` without stale state from the previous one.
 *
 * `is_shared` has no control here, on purpose — the prototype's editor has none
 * either. Every template created through this screen is shared with the whole
 * library, matching it exactly; a private-template toggle is a real feature the
 * prototype never designed, not an oversight to silently add.
 */
import { useEffect, useRef, useState } from 'react';
import { Modal } from '../../components/common/index.jsx';
import { ButtonSpinner, Spinner } from '../../components/common/Loading.jsx';
import { relativeTime } from '../../utils/formatters.js';
import { useLeads } from '../leads/useLeads.js';
import { useMergeFields, usePreviewEmail } from './useEmailTemplates.js';

const TOOLBAR = [
  { cmd: 'bold', icon: 'bi-type-bold', title: 'Bold' },
  { cmd: 'italic', icon: 'bi-type-italic', title: 'Italic' },
  { cmd: 'underline', icon: 'bi-type-underline', title: 'Underline' },
  null,
  { cmd: 'insertUnorderedList', icon: 'bi-list-ul', title: 'Bulleted list' },
  { cmd: 'insertOrderedList', icon: 'bi-list-ol', title: 'Numbered list' },
  { cmd: 'createLink', icon: 'bi-link-45deg', title: 'Insert link' },
  { cmd: 'removeFormat', icon: 'bi-eraser', title: 'Clear formatting' },
];

export default function EmailTemplateEditor({
  initial, isNew, folders, nameTaken, busy, canEdit, canDelete,
  onSave, onDelete, onDuplicate,
}) {
  const [form, setForm] = useState(() => ({
    folder: initial?.folder ?? 'Sales',
    name: initial?.name ?? '',
    description: initial?.description ?? '',
    subject: initial?.subject ?? '',
    include_signature: initial?.include_signature ?? true,
  }));
  const [error, setError] = useState('');
  const [mergeOpen, setMergeOpen] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewLeadId, setPreviewLeadId] = useState('');
  const bodyRef = useRef(null);
  const subjectRef = useRef(null);
  const mergeBox = useRef(null);
  const savedRange = useRef(null);
  const lastFocus = useRef('body');

  const { data: mergeData } = useMergeFields();
  const families = mergeData?.families ?? [];

  const { data: leadsData } = useLeads({ limit: 25, sort: '-created_at' });
  const previewLeads = leadsData?.data ?? [];
  const preview = usePreviewEmail();

  useEffect(() => {
    if (previewLeads.length && !previewLeadId) setPreviewLeadId(previewLeads[0].id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [previewLeads.length]);

  // The single trigger for resolving a preview: opening the modal, picking a
  // different lead, or the leads list finishing its load after the modal was
  // already open all land here, instead of each call site re-implementing when
  // a preview is "due".
  useEffect(() => {
    if (!previewOpen || !previewLeadId) return;
    preview.mutate({
      subject: form.subject,
      body_html: bodyRef.current?.innerHTML ?? '',
      context: { lead_id: previewLeadId },
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [previewOpen, previewLeadId]);

  useEffect(() => {
    if (!mergeOpen) return undefined;
    const away = (e) => { if (!mergeBox.current?.contains(e.target)) setMergeOpen(false); };
    const esc = (e) => { if (e.key === 'Escape') setMergeOpen(false); };
    document.addEventListener('mousedown', away);
    document.addEventListener('keydown', esc);
    return () => {
      document.removeEventListener('mousedown', away);
      document.removeEventListener('keydown', esc);
    };
  }, [mergeOpen]);

  useEffect(() => {
    if (bodyRef.current) {
      bodyRef.current.innerHTML = initial?.body_html ?? '<p>Hi {{lead.first_name}},</p><p></p>';
    }
    // Runs once per mount — the parent keys this component by selection, so a
    // new template means a fresh mount, not a re-render of this one.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

  const trimmedName = form.name.trim();
  const duplicate = Boolean(trimmedName) && nameTaken(trimmedName);

  function captureRange() {
    const sel = window.getSelection();
    if (sel && sel.rangeCount && bodyRef.current?.contains(sel.anchorNode)) {
      savedRange.current = sel.getRangeAt(0);
    }
  }

  function runCommand(cmd) {
    bodyRef.current.focus();
    if (savedRange.current) {
      const sel = window.getSelection();
      sel.removeAllRanges();
      sel.addRange(savedRange.current);
    }
    if (cmd === 'createLink') {
      // eslint-disable-next-line no-alert
      const url = window.prompt('Link URL', 'https://');
      if (url) document.execCommand('createLink', false, url);
      return;
    }
    document.execCommand(cmd, false, null);
  }

  function insertToken(token) {
    if (lastFocus.current === 'subject') {
      const el = subjectRef.current;
      const start = el.selectionStart ?? form.subject.length;
      const end = el.selectionEnd ?? form.subject.length;
      const next = form.subject.slice(0, start) + token + form.subject.slice(end);
      setForm((f) => ({ ...f, subject: next }));
      requestAnimationFrame(() => {
        el.focus();
        el.setSelectionRange(start + token.length, start + token.length);
      });
      return;
    }
    bodyRef.current.focus();
    if (savedRange.current) {
      const sel = window.getSelection();
      sel.removeAllRanges();
      sel.addRange(savedRange.current);
    }
    document.execCommand('insertText', false, token);
  }

  function handleSubmit(e) {
    e.preventDefault();
    setError('');
    if (!trimmedName) { setError('Template name is required.'); return; }
    if (duplicate) { setError('Choose a unique template name.'); return; }
    if (!form.subject.trim()) { setError('A subject line is required.'); return; }

    onSave({
      folder: form.folder.trim() || 'Sales',
      name: trimmedName,
      description: form.description.trim() || null,
      subject: form.subject,
      body_html: bodyRef.current.innerHTML,
      include_signature: form.include_signature,
      ...(isNew ? { is_shared: true } : {}),
    });
  }

  return (
    <form className="p-3" noValidate onSubmit={handleSubmit}>
      <div className="d-flex align-items-center gap-2 mb-3 flex-wrap">
        <div className="min-w-0">
          <div className="eyebrow">{isNew ? 'New template' : 'Edit template'}</div>
          <div className="t-md fw-700">{isNew ? 'Unsaved' : initial?.name ?? '—'}</div>
        </div>
        <div className="ms-auto d-flex gap-2">
          <button type="button" className="btn btn-soft btn-sm-x" onClick={() => setPreviewOpen(true)}>
            <i className="bi bi-eye me-1" aria-hidden="true" />Preview
          </button>
          {!isNew && (
            <button type="button" className="btn btn-soft btn-sm-x" onClick={() => onDuplicate(initial)}>
              <i className="bi bi-files me-1" aria-hidden="true" />Clone
            </button>
          )}
          {!isNew && canDelete && (
            <button type="button" className="btn btn-delete btn-sm-x" onClick={onDelete} title="Delete">
              <i className="bi bi-trash" aria-hidden="true" />
            </button>
          )}
          {canEdit && (
            <button type="submit" className="btn btn-save btn-sm-x" disabled={busy}>
              {busy && <ButtonSpinner />}{busy ? 'Saving…' : 'Save'}
            </button>
          )}
        </div>
      </div>

      <div className="row g-3 mb-3">
        <div className="col-md-6">
          <label className="lbl" htmlFor="etName">Template name <span className="text-danger">*</span></label>
          <input
            id="etName" className={`form-control${trimmedName ? (duplicate ? ' is-invalid' : ' is-valid') : ''}`}
            placeholder="e.g. Renewal outreach" autoComplete="off" disabled={!canEdit}
            value={form.name} onChange={set('name')}
          />
          <div className={`t-2xs mt-1 ${trimmedName ? (duplicate ? 'text-danger' : 'text-success') : 'ink-3'}`}>
            {!trimmedName
              ? 'Must be unique across the library.'
              : duplicate
                ? <><i className="bi bi-x-circle-fill me-1" aria-hidden="true" />Another template already uses this name.</>
                : <><i className="bi bi-check-circle-fill me-1" aria-hidden="true" />Name is available.</>}
          </div>
        </div>
        <div className="col-md-3">
          <label className="lbl" htmlFor="etFolder">Folder</label>
          <input
            id="etFolder" className="form-control" list="etFolders" placeholder="Sales" disabled={!canEdit}
            value={form.folder} onChange={set('folder')}
          />
          <datalist id="etFolders">
            {folders.map((f) => <option key={f} value={f} />)}
          </datalist>
        </div>
        <div className="col-md-3">
          <label className="lbl">Usage</label>
          <div className="d-flex align-items-center gap-2" style={{ height: 37 }}>
            <span className="pill pill-brand">{initial?.usage_count ?? 0} sends</span>
          </div>
        </div>
        <div className="col-12">
          <label className="lbl" htmlFor="etDesc">Description</label>
          <input
            id="etDesc" className="form-control" placeholder="When should someone reach for this template?"
            disabled={!canEdit} value={form.description} onChange={set('description')}
          />
        </div>
        <div className="col-12">
          <label className="lbl" htmlFor="etSubject">Subject <span className="text-danger">*</span></label>
          <input
            id="etSubject" ref={subjectRef} className="form-control" placeholder="Subject line — merge fields allowed"
            disabled={!canEdit} value={form.subject} onChange={set('subject')}
            onFocus={() => { lastFocus.current = 'subject'; }}
          />
        </div>
      </div>

      <label className="lbl">Body</label>
      <div className="editor mb-2">
        <div className="editor-bar">
          {TOOLBAR.map((t, i) => t === null
            ? <span key={`sep-${i}`} className="editor-sep" />
            : (
              <button
                key={t.cmd} type="button" title={t.title} disabled={!canEdit}
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => runCommand(t.cmd)}
              >
                <i className={`bi ${t.icon}`} aria-hidden="true" />
              </button>
            ))}
          <span className="editor-sep" />
          <div className="dropdown" ref={mergeBox}>
            <button
              type="button" className={`merge-btn${mergeOpen ? ' active' : ''}`} disabled={!canEdit}
              onClick={() => setMergeOpen((v) => !v)} aria-expanded={mergeOpen}
            >
              <i className="bi bi-braces" aria-hidden="true" /> Merge field
            </button>
            <ul className={`dropdown-menu ${mergeOpen ? 'show' : ''}`} style={{ maxHeight: 300, overflowY: 'auto' }}>
              {families.map((group) => (
                <li key={group.key}>
                  <h6 className="dropdown-header">{group.label}</h6>
                  <ul className="list-unstyled mb-0">
                    {group.tokens.map((f) => (
                      <li key={f.token}>
                        <a
                          className="dropdown-item" href="#insert-merge-field"
                          onClick={(e) => { e.preventDefault(); insertToken(f.token); setMergeOpen(false); }}
                        >
                          <span className="fw-600">{f.label}</span>
                          <span className="t-2xs ink-4 d-block">{f.token}</span>
                        </a>
                      </li>
                    ))}
                  </ul>
                </li>
              ))}
            </ul>
          </div>
        </div>
        <div
          ref={bodyRef} className="editor-body" contentEditable={canEdit} role="textbox" aria-multiline="true"
          data-placeholder="Write the message…" suppressContentEditableWarning
          onFocus={() => { lastFocus.current = 'body'; }}
          onKeyUp={captureRange} onMouseUp={captureRange} onBlur={captureRange}
        />
      </div>

      <div className="d-flex flex-wrap align-items-center gap-2 mb-2">
        <div className="form-check ms-auto">
          <input
            className="form-check-input" type="checkbox" id="etSig" disabled={!canEdit}
            checked={form.include_signature}
            onChange={(e) => setForm((f) => ({ ...f, include_signature: e.target.checked }))}
          />
          <label className="form-check-label" htmlFor="etSig">Append sender signature on send</label>
        </div>
      </div>

      {error && (
        <div className="alert alert-danger py-2 px-3 t-xs" role="alert">
          <i className="bi bi-exclamation-circle me-1" aria-hidden="true" />{error}
        </div>
      )}
      <div className="t-2xs ink-4 d-flex align-items-start gap-1 mt-2">
        <i className="bi bi-info-circle mt-1" aria-hidden="true" />
        <span>
          {isNew || !initial
            ? 'Not saved yet.'
            : <>Last edited {relativeTime(initial.updated_at)}{initial.updated_by ? ` by ${initial.updated_by.full_name}` : ''}.</>}
        </span>
      </div>

      <Modal
        open={previewOpen} onClose={() => setPreviewOpen(false)} title="Template preview" size="lg"
        footer={<button type="button" className="btn btn-ghost btn-sm-x" onClick={() => setPreviewOpen(false)}>Close</button>}
      >
        <div className="t-xs ink-3 mb-3">Merge fields resolved against a real lead — nothing is sent.</div>
        <label className="lbl" htmlFor="etPvLead">Preview as</label>
        <select
          id="etPvLead" className="form-select mb-3" value={previewLeadId}
          onChange={(e) => setPreviewLeadId(e.target.value)}
        >
          {previewLeads.length === 0 && <option value="">No leads yet</option>}
          {previewLeads.map((l) => (
            <option key={l.id} value={l.id}>{l.full_name} — {l.company_name}</option>
          ))}
        </select>
        {preview.isPending ? (
          <Spinner />
        ) : preview.data ? (
          <div className="mail-preview">
            <div className="mp-head">
              <div className="t-2xs ink-4">Subject</div>
              <div className="t-sm fw-650" dangerouslySetInnerHTML={{ __html: preview.data.subject }} />
              {preview.data.unresolved_tokens.length > 0 && (
                <div className="t-2xs mt-2" style={{ color: 'var(--rose)' }}>
                  <i className="bi bi-exclamation-triangle-fill me-1" aria-hidden="true" />
                  Unresolved: {preview.data.unresolved_tokens.join(', ')}
                </div>
              )}
            </div>
            <div dangerouslySetInnerHTML={{ __html: preview.data.body_html }} />
          </div>
        ) : (
          <div className="t-sm ink-3">Pick a lead above to resolve this template against.</div>
        )}
      </Modal>
    </form>
  );
}
